import React, { useState, useCallback } from 'react';
import styled, { css } from '@emotion/native';
import Cropper from 'react-easy-crop';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import {
  white,
  green,
  lightGrey,
  sand,
  lightGreen,
} from '@/shared/styles/colors';
import Button from './Button';
import { BodyText } from './StyledText';
import useBreakpoint from '../hooks/useBreakpoint';
import SentryService from '@/services/sentry/Sentry.service';

interface ImageCropperProps {
  imageUri: string;
  onCropComplete: (croppedUri: string) => void;
  onCancel: () => void;
  aspectRatio?: number;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CropData {
  originX: number;
  originY: number;
  width: number;
  height: number;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  imageUri,
  onCropComplete,
  onCancel,
  aspectRatio = 1,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(
    null,
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const { isDesktop } = useBreakpoint();

  const onCropChangeComplete = useCallback(
    (_: CropArea, croppedAreaPixels: CropArea) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const handleCrop = useCallback(async () => {
    if (!croppedAreaPixels) return;

    setIsProcessing(true);

    try {
      const cropData: CropData = {
        originX: croppedAreaPixels.x,
        originY: croppedAreaPixels.y,
        width: croppedAreaPixels.width,
        height: croppedAreaPixels.height,
      };

      // Crop the image, don't compress yet
      const croppedResult = await manipulateAsync(
        imageUri,
        [
          {
            crop: cropData,
          },
        ],
        {
          format: SaveFormat.JPEG,
          compress: 1.0,
        },
      );

      // Calculate target dimensions to keep file size reasonable
      const maxDimension = 2000;
      const targetWidth = Math.min(croppedAreaPixels.width, maxDimension);
      const targetHeight = Math.min(croppedAreaPixels.height, maxDimension);

      // Maintain aspect ratio
      const aspectRatio = croppedAreaPixels.width / croppedAreaPixels.height;
      let finalWidth = targetWidth;
      let finalHeight = targetHeight;

      if (aspectRatio > 1) {
        // Landscape
        finalHeight = Math.round(finalWidth / aspectRatio);
      } else {
        // Portrait or square
        finalWidth = Math.round(finalHeight * aspectRatio);
      }

      // Resize and compress the image
      const finalResult = await manipulateAsync(
        croppedResult.uri,
        [
          {
            resize: {
              width: finalWidth,
              height: finalHeight,
            },
          },
        ],
        {
          format: SaveFormat.JPEG,
          compress: 0.6,
        },
      );

      onCropComplete(finalResult.uri);
    } catch (error) {
      SentryService.captureError('Error cropping image', error);
      onCancel();
    } finally {
      setIsProcessing(false);
    }
  }, [croppedAreaPixels, imageUri, onCropComplete, onCancel]);

  return (
    <Container>
      <CropperContainer>
        <CloseButton onPress={onCancel}>
          <CloseIcon source={require('@/assets/images/CloseIcon.png')} />
        </CloseButton>
        <Cropper
          image={imageUri}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropChangeComplete}
          style={{
            containerStyle: {
              width: '100%',
              height: '100%',
              position: 'relative',
              backgroundColor: green,
            },
          }}
        />
      </CropperContainer>

      <ControlsContainer>
        <Instructions>
          Adjust your image by dragging to reposition. Use your scroll wheel or
          pinch to zoom in or out.{'\n'}
          When you're happy with the framing, select 'Crop' to save.
        </Instructions>

        <ButtonContainer>
          <Button
            text="Cancel"
            onPress={onCancel}
            size="medium"
            customStyle={[
              customButtonStyles,
              { marginRight: isDesktop ? 30 : 10 },
            ]}
            variant="secondary"
          />
          <Button
            text={isProcessing ? 'Cropping...' : 'Crop Image'}
            onPress={handleCrop}
            disabled={isProcessing || !croppedAreaPixels}
            loading={isProcessing}
            size="medium"
            customStyle={customButtonStyles}
          />
        </ButtonContainer>
      </ControlsContainer>
    </Container>
  );
};

const Container = styled.View({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
});

const CropperContainer = styled.View({
  flex: 1,
  position: 'relative',
  minHeight: 400,
  backgroundColor: white,
  margin: 20,
  borderRadius: 8,
  overflow: 'hidden',
});

const ControlsContainer = styled.View({
  padding: 20,
  backgroundColor: sand,
  borderTopWidth: 1,
  borderTopColor: lightGrey,
});

const Instructions = styled(BodyText)({
  textAlign: 'center',
  marginBottom: 20,
  color: lightGreen,
  fontSize: 14,
  lineHeight: 20,
});

const ButtonContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
});

const customButtonStyles = css({
  width: 175,
  marginBottom: 17,
  height: 48,
  borderWidth: 2,
});

const CloseButton = styled.TouchableOpacity({
  position: 'absolute',
  top: 20,
  right: 20,
  zIndex: 1001,
  justifyContent: 'center',
  alignItems: 'center',
});

const CloseIcon = styled.Image({
  width: 24,
  height: 24,
  resizeMode: 'contain',
});

export default ImageCropper;
